import { getDistance } from '../utils/math.js';
import { BERSERKER_CONFIG } from '../config.js';
import { audioManager } from '../systems/AudioManager.js';
import { events } from '../ai/SoldierStateMachine.js';

/**
 * CollisionSystem - Handles all collision detection and response
 *
 * Key Features:
 * - Ray-cast collision detection for fast projectiles
 * - Prevents friendly fire (team-based filtering)
 * - Knockback physics from base missiles
 * - Corpse collection for instant level-ups
 * - Explosion effects on impact
 * - Special berserker bonuses for base destruction
 *
 * Performance:
 * - Uses line-circle intersection for accurate high-speed projectile hits
 * - Prevents projectiles phasing through targets at high velocities
 * - Checks both current position AND path traveled since last frame
 */
export class CollisionSystem {
  /**
   * Create collision system with event callbacks
   * @param {Function|null} onKill - Callback when unit kills enemy (teamId, victimTeamId)
   * @param {Function|null} onCorpseCreate - Callback to create corpse (x, y, deadSoldier)
   */
  constructor(onKill = null, onCorpseCreate = null) {
    this.onKill = onKill;
    this.onCorpseCreate = onCorpseCreate;
  }

  /**
   * Check for projectile hits against soldiers and bases
   *
   * Uses two collision methods:
   * 1. Point collision - check current projectile position vs target
   * 2. Ray-cast collision - check projectile's path this frame vs target
   *
   * The second method prevents fast projectiles from "phasing through" targets.
   *
   * @param {Projectile[]} projectiles - All active projectiles
   * @param {Soldier[]} soldiers - All alive soldiers
   * @param {TeamBase[]} bases - All bases (alive or destroyed)
   * @returns {Object[]} Array of hit objects {projectile, soldier|base, projectileIndex}
   */
  checkProjectileHits(projectiles, soldiers, bases = []) {
    const hits = [];

    for (let i = projectiles.length - 1; i >= 0; i--) {
      const projectile = projectiles[i];
      
      // Check hits only after projectile has traveled a bit
      if (projectile.lifetime <= 3) continue;

      for (let soldier of soldiers) {
        if (!soldier.alive || soldier.health <= 0 || soldier === projectile.owner) continue;
        
        // Skip teammates to prevent friendly fire
        if (soldier.team.id === projectile.owner.team.id) continue;

        // Check both current position and path traveled this frame
        const currentDistance = getDistance(projectile, soldier);
        const hitRadius = soldier.size + 2;
        
        // Current position hit check
        if (currentDistance < hitRadius) {
          hits.push({
            projectile: projectile,
            soldier: soldier,
            projectileIndex: i
          });
          break; // Projectile can only hit one soldier
        }
        
        // Ray casting - check if projectile path intersected target
        if (projectile.prevX !== undefined && projectile.prevY !== undefined) {
          const pathHit = this.checkLineCircleIntersection(
            projectile.prevX, projectile.prevY,
            projectile.x, projectile.y,
            soldier.x, soldier.y, hitRadius
          );
          
          if (pathHit) {
            hits.push({
              projectile: projectile,
              soldier: soldier,
              projectileIndex: i
            });
            break; // Projectile can only hit one soldier
          }
        }
      }

      // Check bases
      for (let base of bases) {
        if (!base.alive || base.team.id === projectile.owner.team.id) continue;
        
        // Check both current position and path traveled this frame
        const currentDistance = getDistance(projectile, base);
        const hitRadius = base.size;
        
        // Current position hit check
        if (currentDistance < hitRadius) {
          hits.push({
            projectile: projectile,
            base: base,
            projectileIndex: i
          });
          break;
        }
        
        // Ray casting - check if projectile path intersected base
        if (projectile.prevX !== undefined && projectile.prevY !== undefined) {
          const pathHit = this.checkLineCircleIntersection(
            projectile.prevX, projectile.prevY,
            projectile.x, projectile.y,
            base.x, base.y, hitRadius
          );
          
          if (pathHit) {
            hits.push({
              projectile: projectile,
              base: base,
              projectileIndex: i
            });
            break;
          }
        }
      }
    }

    return hits;
  }

  /**
   * Check if a line segment (projectile path) intersects with a circle (target)
   *
   * Mathematical approach:
   * 1. Project circle center onto the line segment
   * 2. Find closest point on line to circle center
   * 3. Check if distance from circle center to closest point <= radius
   *
   * This prevents fast projectiles from "tunneling" through targets.
   *
   * @param {number} lineX1 - Line start X (projectile previous position)
   * @param {number} lineY1 - Line start Y (projectile previous position)
   * @param {number} lineX2 - Line end X (projectile current position)
   * @param {number} lineY2 - Line end Y (projectile current position)
   * @param {number} circleX - Circle center X (target position)
   * @param {number} circleY - Circle center Y (target position)
   * @param {number} radius - Circle radius (target hit radius)
   * @returns {boolean} True if line intersects circle
   */
  checkLineCircleIntersection(lineX1, lineY1, lineX2, lineY2, circleX, circleY, radius) {
    // Vector from line start to circle center
    const dx1 = circleX - lineX1;
    const dy1 = circleY - lineY1;
    
    // Vector of the line
    const dx2 = lineX2 - lineX1;
    const dy2 = lineY2 - lineY1;
    
    // Length squared of the line
    const lengthSq = dx2 * dx2 + dy2 * dy2;
    
    // If line has no length, just check distance to start point
    if (lengthSq === 0) {
      return Math.sqrt(dx1 * dx1 + dy1 * dy1) <= radius;
    }
    
    // Project circle center onto the line
    const t = Math.max(0, Math.min(1, (dx1 * dx2 + dy1 * dy2) / lengthSq));
    
    // Find closest point on line segment
    const closestX = lineX1 + t * dx2;
    const closestY = lineY1 + t * dy2;
    
    // Check distance from circle center to closest point
    const distanceX = circleX - closestX;
    const distanceY = circleY - closestY;
    const distanceSq = distanceX * distanceX + distanceY * distanceY;
    
    return distanceSq <= radius * radius;
  }

  /**
   * Process all projectile hits and apply damage/effects
   *
   * Handles:
   * - Damage application to soldiers/bases
   * - Knockback from base missiles (strong directional force)
   * - Explosion visual effects
   * - Corpse creation when soldiers die
   * - Base destruction bonuses for berserkers
   * - Sound effects via callbacks
   *
   * Special cases:
   * - Base missiles (isPerfectHoming) knock back soldiers and trigger fleeing
   * - Berserkers get bonus levels for destroying bases
   * - Base missiles that kill increase the base's perception range
   *
   * @param {Object[]} hits - Hit objects from checkProjectileHits()
   * @param {Projectile[]} projectiles - All projectiles (for indexing)
   * @param {Soldier[]} soldiers - All soldiers
   * @param {TeamBase[]} bases - All bases
   */
  processHits(hits, projectiles, soldiers, bases) {
    for (let hit of hits) {
      const { projectile, soldier, base, projectileIndex } = hit;

      if (soldier) {
        // Apply knockback for perfect homing projectiles (base missiles)
        if (projectile.isPerfectHoming) {
          const knockbackForce = 18; // Strong knockback
          
          // Use projectile's velocity direction (trajectory) for knockback
          const projectileSpeed = Math.sqrt(projectile.vx * projectile.vx + projectile.vy * projectile.vy);
          if (projectileSpeed > 0) {
            const directionX = projectile.vx / projectileSpeed;
            const directionY = projectile.vy / projectileSpeed;
            
            // Completely replace soldier's velocity with knockback
            soldier.vx = directionX * knockbackForce;
            soldier.vy = directionY * knockbackForce;
            soldier.knockbackTime = 15; // Longer knockback duration
            
            // Clear current target
            soldier.target = null;
            soldier.lastDamageTime = Date.now(); // Reset damage timer for fleeing behavior
            
            // Store the attacking base for fleeing direction
            if (projectile.owner && projectile.owner.constructor.name === 'TeamBase') {
              soldier.fleeFromBase = projectile.owner;
            }
            
            // Send LOW_HEALTH event to state machine to trigger proper fleeing
            if (soldier.ai && soldier.ai.sendEvent) {
              soldier.ai.sendEvent(events.LOW_HEALTH);
            }
          }
        }
        
        const killed = soldier.takeDamage(projectile.damage, projectile.owner, soldiers);
        
        // If killed by a base missile, increase the base's perception range
        if (killed && projectile.isPerfectHoming && projectile.owner && projectile.owner.constructor.name === 'TeamBase') {
          projectile.owner.onMissileKill();
        }
        
        // Trigger explosion effect for all projectiles on impact
        projectile.explode();
        // Don't remove from array - let the projectile's update method handle cleanup
        if (killed && this.onCorpseCreate) {
          this.onCorpseCreate(soldier.x, soldier.y, soldier);
        }
        if (killed && this.onKill) {
          this.onKill(projectile.owner.team.id, soldier.team.id);
        }
      } else if (base) {
        const destroyed = base.takeDamage(projectile.damage);
        
        // Trigger explosion effect for perfect homing projectiles on base impact
        // Trigger explosion for all projectiles
        projectile.explode();
        // Don't remove from array - let the projectile's update method handle cleanup after explosion
        
        // Award bonus levels to berserker who destroys a base
        if (destroyed && projectile.owner && projectile.owner.isBerserker) {
          for (let i = 0; i < BERSERKER_CONFIG.BASE_DESTRUCTION_LEVELS; i++) {
            projectile.owner.levelUp();
          }
          audioManager.playBerserkerBaseDestroy();
        }
      }
    }
  }

  /**
   * Check which soldiers can collect which corpses
   *
   * Corpses grant instant level-up and full ammo to collectors.
   * Only certain soldiers can collect corpses (based on state and distance).
   *
   * @param {Soldier[]} soldiers - All alive soldiers
   * @param {Corpse[]} corpses - All active corpses
   * @returns {Object[]} Array of collection objects {soldier, corpse, corpseIndex}
   */
  checkCorpseCollection(soldiers, corpses) {
    const collections = [];
    
    for (let soldier of soldiers) {
      if (!soldier.alive || soldier.health <= 0) continue;
      
      for (let i = corpses.length - 1; i >= 0; i--) {
        const corpse = corpses[i];
        if (!corpse.active) continue;
        
        if (corpse.canBeCollectedBy(soldier)) {
          collections.push({
            soldier: soldier,
            corpse: corpse,
            corpseIndex: i
          });
          break; // Soldier can only collect one corpse per frame
        }
      }
    }
    
    return collections;
  }

  /**
   * Process corpse collections - apply level-ups and remove corpses
   *
   * @param {Object[]} collections - Collection objects from checkCorpseCollection()
   * @param {Corpse[]} corpses - All corpses (will be modified by removing collected ones)
   */
  processCorpseCollections(collections, corpses) {
    for (let collection of collections) {
      const { soldier, corpse, corpseIndex } = collection;
      
      // Soldier collects the corpse and levels up
      corpse.collect(soldier);
      
      // Remove corpse from array
      corpses.splice(corpseIndex, 1);
    }
  }

  /**
   * Main update loop - check and process all collisions
   *
   * Called every frame to detect and resolve collisions.
   * Order matters: process projectile hits before corpse collections
   * (so newly created corpses aren't collected in same frame).
   *
   * @param {Projectile[]} projectiles - All active projectiles
   * @param {Soldier[]} soldiers - All alive soldiers
   * @param {Corpse[]} corpses - All active corpses
   * @param {TeamBase[]} bases - All bases
   */
  update(projectiles, soldiers, corpses = [], bases = []) {
    const hits = this.checkProjectileHits(projectiles, soldiers, bases);
    this.processHits(hits, projectiles, soldiers, bases);
    
    if (corpses.length > 0) {
      const collections = this.checkCorpseCollection(soldiers, corpses);
      this.processCorpseCollections(collections, corpses);
    }
  }
}